import type { DemoData, EnrichedPart, SupplierPriceUpdate } from "../domain/types";
import {
    CF013_RULE_ID,
    DQ001_RULE_ID,
    DQ002_RULE_ID,
    DQ003_RULE_ID,
    DQ004_RULE_ID,
} from "../appglueIntegration/policies/statementRulePolicyRegistry";
import {
    CF014_RULE_ID,
    TR005_RULE_ID,
    TR006_RULE_ID,
    TR007_RULE_ID,
    TR008_RULE_ID,
    WF009_RULE_ID,
    WF010_RULE_ID,
    WF011_RULE_ID,
    WF012_RULE_ID,
} from "../appglueIntegration/policies/tableTextRulePolicyRegistry";
import {
    applyRuleIntegrationRuntimeDemoDataProjection,
    createRuleIntegrationRuntimeRegistry,
    evaluateRuleIntegrationRuntime,
    type RuleIntegrationRuntimeRegistry,
} from "../appglueIntegration/ruleIntegrationRuntime";
import type { RuleIntegrationComparison } from "../appglueIntegration/ruleIntegrationTypes";
import type {
    DecisionComparison,
    DecisionEvent,
    DecisionEventEvaluation,
    DecisionEventOverallStatus,
    DecisionEventRuleSummary,
    DecisionEventSummary,
    DecisionEventType,
    DecisionRuleIntegrationEvidence,
    DecisionRuleOutcome,
} from "../domain/decisionEventTypes";
import type { DemoOutputQueue, DemoRule, DemoRuleStatus } from "../domain/ruleTypes";
import {
    SEEDED_RULE_LOGIC_BY_ID,
    type RuleLogicDefinitionMap,
} from "../rules/ruleLogicDefinitions";
import { SEEDED_RULES } from "../rules/seedRules";

const DEFAULT_MAX_EVENTS = 10000;
const DEFAULT_PART_EVENT_RATIO = 0.6;
const BASE_OCCURRED_AT_MS = Date.UTC(2026, 4, 1, 12, 0, 0);
const RULES_BY_ID = Object.fromEntries(SEEDED_RULES.map((rule) => [rule.id, rule] as const));
const DATA_QUALITY_RULE_IDS = [DQ001_RULE_ID, DQ002_RULE_ID, DQ003_RULE_ID, DQ004_RULE_ID] as const;

type DecisionEventEvaluationOptions = {
    maxEvents?: number;
    ruleDefinitionsById?: RuleLogicDefinitionMap;
    rules?: DemoRule[];
};

const emptyRuleSummary = (): DecisionEventRuleSummary => ({
    pass: 0,
    flagged: 0,
    rejected: 0,
    skipped: 0,
    errored: 0,
});

const emptyEventTypeCounts = (): DecisionEventSummary["byEventType"] => ({
    part_enrichment_received: 0,
    supplier_price_update_received: 0,
    lifecycle_status_change: 0,
    compliance_change_detected: 0,
});

const statusForEvent = (outcomes: DecisionRuleOutcome[]): DecisionEventOverallStatus => {
    if (outcomes.some((outcome) => outcome.status === "rejected")) return "rejected";
    if (outcomes.some((outcome) => outcome.status === "flagged")) return "flagged";
    return "pass";
};

const primaryOutcome = (outcomes: DecisionRuleOutcome[]): DecisionRuleOutcome | undefined =>
    outcomes.find((outcome) => outcome.status === "rejected") ??
    outcomes.find((outcome) => outcome.status === "flagged") ??
    outcomes.find((outcome) => outcome.status === "pass");

const comparison = (label: string, expected: string, actual: string, ok?: boolean): DecisionComparison => ({
    label,
    expected,
    actual,
    ok,
});

const outcome = (params: {
    rule: DemoRule;
    status: DemoRuleStatus;
    reasonCode: string;
    explanation: string;
    outputQueue?: DemoOutputQueue;
    comparisons?: DecisionComparison[];
    integrationEvidence?: DecisionRuleIntegrationEvidence;
}): DecisionRuleOutcome => ({
    ruleId: params.rule.id,
    ruleName: params.rule.name,
    status: params.status,
    reasonCode: params.reasonCode,
    explanation: params.explanation,
    outputQueue: params.outputQueue,
    comparisons: params.comparisons ?? [],
    integrationEvidence: params.integrationEvidence,
});

const eventTypeForPart = (part: EnrichedPart): DecisionEventType => {
    if (part.lifecycleStatus === "EOL" || part.lifecycleStatus === "NRND") return "lifecycle_status_change";
    if (part.hasComplianceChange) return "compliance_change_detected";
    return "part_enrichment_received";
};

const toDecisionComparisons = (comparisons: RuleIntegrationComparison[]): DecisionComparison[] =>
    comparisons.map((item) => comparison(item.label, item.expected, item.actual, item.ok));

const buildIntegratedRuleOutcome = (
    ruleId: string,
    row: unknown,
    integrationRuntime: RuleIntegrationRuntimeRegistry,
): DecisionRuleOutcome => {
    const evaluation = evaluateRuleIntegrationRuntime(integrationRuntime, ruleId, row);
    const runtime = integrationRuntime[ruleId];
    const rule = RULES_BY_ID[ruleId];
    if (!rule) {
        throw new Error(`No demo rule catalog entry exists for ${ruleId}.`);
    }

    return outcome({
        rule,
        status: evaluation.status,
        reasonCode: evaluation.reasonCode,
        explanation: evaluation.explanation,
        outputQueue: evaluation.outputQueue,
        comparisons: toDecisionComparisons(evaluation.comparisons),
        integrationEvidence: runtime
            ? {
                  runtime: evaluation.runtimeEvidence.runtime,
                  executableType: evaluation.runtimeEvidence.executableType,
                  configSource: runtime.configSource,
                  adapterResult: evaluation.evidence,
              }
            : undefined,
    });
};

const projectRuleOutcomesForCatalog = (
    canonicalOutcomes: DecisionRuleOutcome[],
    rules: DemoRule[],
): DecisionRuleOutcome[] =>
    rules
        .map((rule) => {
            const sourceRuleId = rule.sourceRuleId ?? rule.id;
            const sourceOutcome = canonicalOutcomes.find((candidate) => candidate.ruleId === sourceRuleId);
            if (!sourceOutcome) return null;

            return {
                ...sourceOutcome,
                ruleId: rule.id,
                ruleName: rule.name,
            };
        })
        .filter((outcome): outcome is DecisionRuleOutcome => !!outcome);

const sourcePriorityAttributeForPart = (part: EnrichedPart): string => {
    if (part.hasComplianceChange) return "rohsStatus";
    if (part.lifecycleStatus === "EOL" || part.lifecycleStatus === "NRND") return "lifecycleStatus";
    if (!part.packageType) return "packageType";
    return "pricing";
};

const buildPartOutcomes = (
    data: DemoData,
    part: EnrichedPart,
    indexes: {
        catalogByPartId: Set<string>;
        opportunityPartIds: Set<string>;
        purchaseOrderPartIds: Set<string>;
    },
    integrationRuntime: RuleIntegrationRuntimeRegistry,
    activeRules: DemoRule[],
): DecisionRuleOutcome[] => {
    const textMergeRow = { data, part };
    const hasCommercialActivity =
        indexes.opportunityPartIds.has(part.partId) || indexes.purchaseOrderPartIds.has(part.partId);
    const canonicalOutcomes = [
        ...DATA_QUALITY_RULE_IDS.map((ruleId) => buildIntegratedRuleOutcome(ruleId, part, integrationRuntime)),
        buildIntegratedRuleOutcome(TR005_RULE_ID, part, integrationRuntime),
        buildIntegratedRuleOutcome(TR006_RULE_ID, textMergeRow, integrationRuntime),
        buildIntegratedRuleOutcome(TR007_RULE_ID, textMergeRow, integrationRuntime),
        buildIntegratedRuleOutcome(
            WF009_RULE_ID,
            { has_catalog_record: indexes.catalogByPartId.has(part.partId) },
            integrationRuntime,
        ),
        buildIntegratedRuleOutcome(
            WF011_RULE_ID,
            {
                lifecycle_status: part.lifecycleStatus,
                has_open_activity: hasCommercialActivity,
            },
            integrationRuntime,
        ),
        buildIntegratedRuleOutcome(
            WF012_RULE_ID,
            { has_compliance_change: part.hasComplianceChange },
            integrationRuntime,
        ),
        buildIntegratedRuleOutcome(CF013_RULE_ID, part, integrationRuntime),
        buildIntegratedRuleOutcome(
            CF014_RULE_ID,
            {
                attribute_name: sourcePriorityAttributeForPart(part),
                has_source_conflict: part.hasSourceConflict,
            },
            integrationRuntime,
        ),
    ];

    return projectRuleOutcomesForCatalog(canonicalOutcomes, activeRules);
};

const buildPriceOutcomes = (
    update: SupplierPriceUpdate,
    indexes: {
        opportunityPartIds: Set<string>;
    },
    integrationRuntime: RuleIntegrationRuntimeRegistry,
    activeRules: DemoRule[],
): DecisionRuleOutcome[] => {
    const hasOpenOpportunity = indexes.opportunityPartIds.has(update.partId);
    const canonicalOutcomes = [
        buildIntegratedRuleOutcome(TR008_RULE_ID, update, integrationRuntime),
        buildIntegratedRuleOutcome(
            WF010_RULE_ID,
            {
                price_change_pct: update.priceChangePct,
                has_open_opportunity: hasOpenOpportunity,
            },
            integrationRuntime,
        ),
    ];

    return projectRuleOutcomesForCatalog(canonicalOutcomes, activeRules);
};

const downstreamActionForOutcome = (selected: DecisionRuleOutcome | undefined): string => {
    if (!selected) return "No action";
    if (selected.outputQueue === "quarantine_records") return "Quarantine row before publish";
    if (selected.outputQueue === "archived_parts") return "Archive part and block active catalog publish";
    if (selected.outputQueue === "pending_review") return "Route row to review queue";
    if (selected.outputQueue === "procurement_review_queue") return "Hold cost sync for procurement review";
    if (selected.outputQueue === "compliance_review_queue") return "Hold compliance overwrite for review";
    if (selected.outputQueue === "salesforce_product_create_events") return "Create CRM product-review event";
    if (selected.outputQueue === "salesforce_price_alert_events") return "Create CRM margin-risk alert";
    if (selected.outputQueue === "salesforce_obsolescence_alert_events") return "Create CRM replacement-part alert";
    if (selected.outputQueue === "epicor_cost_file_updates") return "Approve ERP cost-file update";
    return selected.status === "pass" ? "Allow downstream processing" : "Route for review";
};

const occurredAt = (index: number): string => new Date(BASE_OCCURRED_AT_MS + index * 60_000).toISOString();

const buildPartEvent = (
    data: DemoData,
    part: EnrichedPart,
    index: number,
    indexes: Parameters<typeof buildPartOutcomes>[2],
    integrationRuntime: RuleIntegrationRuntimeRegistry,
    activeRules: DemoRule[],
): DecisionEvent => {
    const ruleOutcomes = buildPartOutcomes(data, part, indexes, integrationRuntime, activeRules);
    const selected = primaryOutcome(ruleOutcomes);

    return {
        eventId: `EVT-PART-${String(index + 1).padStart(6, "0")}`,
        eventType: eventTypeForPart(part),
        subjectId: part.partId,
        subjectLabel: `${part.manufacturerName} ${part.mpn}`,
        occurredAt: occurredAt(index),
        sourceTable: "enriched_parts",
        primaryRuleId: selected?.ruleId ?? null,
        overallStatus: statusForEvent(ruleOutcomes),
        headline: selected?.explanation ?? "Part row evaluated.",
        downstreamAction: downstreamActionForOutcome(selected),
        outputQueue: selected?.outputQueue,
        contextPreview: {
            mpn: part.mpn,
            manufacturer: part.manufacturerName,
            category: part.category,
            taxonomy_path: part.taxonomyPath,
            lifecycle_status: part.lifecycleStatus,
            confidence_score: part.confidenceScore,
        },
        ruleOutcomes,
    };
};

const buildPriceEvent = (
    update: SupplierPriceUpdate,
    index: number,
    indexes: Parameters<typeof buildPriceOutcomes>[1],
    integrationRuntime: RuleIntegrationRuntimeRegistry,
    activeRules: DemoRule[],
): DecisionEvent => {
    const ruleOutcomes = buildPriceOutcomes(update, indexes, integrationRuntime, activeRules);
    const selected = primaryOutcome(ruleOutcomes);

    return {
        eventId: `EVT-PRICE-${String(index + 1).padStart(6, "0")}`,
        eventType: "supplier_price_update_received",
        subjectId: update.updateId,
        subjectLabel: `${update.manufacturerName} ${update.mpn}`,
        occurredAt: occurredAt(index),
        sourceTable: "supplier_price_updates",
        primaryRuleId: selected?.ruleId ?? null,
        overallStatus: statusForEvent(ruleOutcomes),
        headline: selected?.explanation ?? "Price update evaluated.",
        downstreamAction: downstreamActionForOutcome(selected),
        outputQueue: selected?.outputQueue,
        contextPreview: {
            mpn: update.mpn,
            manufacturer: update.manufacturerName,
            supplier: update.supplierName,
            break_quantity: update.breakQuantity,
            price_change_pct: update.priceChangePct,
            new_cost: update.newCost,
        },
        ruleOutcomes,
    };
};

const summarizeEvents = (events: DecisionEvent[]): DecisionEventSummary => {
    const summary: DecisionEventSummary = {
        totalEvents: events.length,
        pass: 0,
        flagged: 0,
        rejected: 0,
        errored: 0,
        byRule: {},
        byEventType: emptyEventTypeCounts(),
    };

    events.forEach((event) => {
        if (event.overallStatus === "pass") summary.pass += 1;
        if (event.overallStatus === "flagged") summary.flagged += 1;
        if (event.overallStatus === "rejected") summary.rejected += 1;
        if (event.overallStatus === "error") summary.errored += 1;
        summary.byEventType[event.eventType] += 1;

        event.ruleOutcomes.forEach((ruleOutcome) => {
            const ruleSummary = (summary.byRule[ruleOutcome.ruleId] ??= emptyRuleSummary());
            if (ruleOutcome.status === "pass") ruleSummary.pass += 1;
            if (ruleOutcome.status === "flagged") ruleSummary.flagged += 1;
            if (ruleOutcome.status === "rejected") ruleSummary.rejected += 1;
            if (ruleOutcome.status === "skipped") ruleSummary.skipped += 1;
        });
    });

    return summary;
};

const buildSelection = <T>(rows: T[], targetCount: number): T[] => rows.slice(0, Math.max(0, targetCount));

export const buildDecisionEventEvaluation = (
    data: DemoData,
    options: DecisionEventEvaluationOptions = {},
): DecisionEventEvaluation => {
    const maxEvents = Math.max(0, options.maxEvents ?? DEFAULT_MAX_EVENTS);
    const ruleDefinitionsById = options.ruleDefinitionsById ?? SEEDED_RULE_LOGIC_BY_ID;
    const activeRules = options.rules ?? SEEDED_RULES;
    const partTarget = Math.min(data.parts.length, Math.ceil(maxEvents * DEFAULT_PART_EVENT_RATIO));
    const priceTarget = Math.min(data.priceUpdates.length, maxEvents - partTarget);
    const remaining = maxEvents - partTarget - priceTarget;
    const selectedParts = buildSelection(
        data.parts,
        partTarget + Math.min(remaining, Math.max(0, data.parts.length - partTarget)),
    );
    const selectedPrices = buildSelection(data.priceUpdates, priceTarget);
    const indexes = {
        catalogByPartId: new Set(data.catalog.map((catalog) => catalog.partId)),
        opportunityPartIds: new Set(data.opportunities.map((opportunity) => opportunity.partId)),
        purchaseOrderPartIds: new Set(data.purchaseOrders.map((purchaseOrder) => purchaseOrder.partId)),
    };
    const integrationRuntime = createRuleIntegrationRuntimeRegistry(ruleDefinitionsById, data);
    const projectedData = applyRuleIntegrationRuntimeDemoDataProjection(data, integrationRuntime);
    const partEvents = selectedParts.map((part, index) =>
        buildPartEvent(projectedData, part, index, indexes, integrationRuntime, activeRules),
    );
    const priceEvents = selectedPrices.map((update, index) =>
        buildPriceEvent(update, partEvents.length + index, indexes, integrationRuntime, activeRules),
    );
    const events = [...partEvents, ...priceEvents];

    return {
        events,
        summary: summarizeEvents(events),
    };
};
